// components/pages/ComingSoon.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Container from '../common/Container';
import Button from '../common/Button';
import { sendContactMessage } from '../../services/contactService';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: var(--background-primary);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const Card = styled.div`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 3rem;
  max-width: 560px;
  width: 100%;
  box-shadow: var(--card-shadow);
  text-align: center;

  @media (max-width: 576px) {
    padding: 2rem 1.5rem;
  }
`;

const Badge = styled.span`
  display: inline-block;
  background: rgba(240, 180, 50, 0.15);
  color: var(--accent-primary);
  font-size: 13px;
  font-weight: 600;
  padding: 6px 16px;
  border-radius: 20px;
  margin-bottom: 1.5rem;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  color: var(--text-primary);
  margin: 0 0 1rem 0;

  @media (max-width: 576px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0 0 2rem 0;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid var(--border-color);
  margin: 2rem 0;
`;

const FormTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 1.5rem 0;
  text-align: left;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  text-align: left;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
`;

const Input = styled.input`
  padding: 10px 14px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--background-primary);
  color: var(--text-primary);
  font-size: 14px;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: 0 0 0 3px rgba(240, 180, 50, 0.15);
  }
`;

const Textarea = styled.textarea`
  padding: 10px 14px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--background-primary);
  color: var(--text-primary);
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: 0 0 0 3px rgba(240, 180, 50, 0.15);
  }
`;

const FormStatus = styled.div`
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 14px;
  text-align: center;

  &.error {
    background: rgba(229, 57, 53, 0.1);
    color: #e53935;
  }

  &.success {
    background: rgba(67, 160, 71, 0.1);
    color: #43a047;
  }

  &.sending {
    background: rgba(3, 169, 244, 0.1);
    color: #0288d1;
  }
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 14px;
  margin-top: 1.5rem;
  transition: color 0.2s;

  &:hover {
    color: var(--accent-primary);
  }
`;

const ComingSoon = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'Coming Soon - Interest',
    message: ''
  });
  const [status, setStatus] = useState('');
  const [statusClass, setStatusClass] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      setStatus('Please fill in all fields');
      setStatusClass('error');
      return;
    }

    setIsSending(true);
    setStatus('Sending...');
    setStatusClass('sending');

    try {
      await sendContactMessage(formData);
      setStatus("Thanks! We'll notify you when we launch.");
      setStatusClass('success');
      setFormData({ name: '', email: '', subject: 'Coming Soon - Interest', message: '' });
    } catch (error) {
      setStatus(`Error: ${error.message}`);
      setStatusClass('error');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <PageWrapper>
      <Card>
        <Badge>Coming Soon</Badge>
        <Title>We're Almost Ready</Title>
        <Subtitle>
          Sivo is being built to revolutionize sign manufacturing. Leave your details and we'll let you know when we launch.
        </Subtitle>

        <Divider />

        <FormTitle>Get notified when we launch</FormTitle>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Name</Label>
            <Input
              type="text"
              name="name"
              placeholder="Your name"
              value={formData.name}
              onChange={handleChange}
            />
          </FormGroup>

          <FormGroup>
            <Label>Email</Label>
            <Input
              type="email"
              name="email"
              placeholder="your@email.com"
              value={formData.email}
              onChange={handleChange}
            />
          </FormGroup>

          <FormGroup>
            <Label>Message (optional)</Label>
            <Textarea
              name="message"
              placeholder="Tell us about your sign business or what features you're most excited about..."
              value={formData.message}
              onChange={handleChange}
            />
          </FormGroup>

          {status && (
            <FormStatus className={statusClass}>{status}</FormStatus>
          )}

          <Button variant="primary" type="submit" disabled={isSending} fullWidth>
            {isSending ? 'Sending...' : 'Notify Me'}
          </Button>
        </Form>

        <BackLink onClick={() => navigate('/')}>
          &larr; Back to home
        </BackLink>
      </Card>
    </PageWrapper>
  );
};

export default ComingSoon;
